export type Agg = "sum" | "avg" | "count" | "countDistinct" | "min" | "max" | "none";
export type Unit = "EUR" | "EUR/1kClicks" | "ratio" | "count" | "ms" | "EUR/1kImpressions";

export interface DimensionDef {
  id: string;
  label: string;
  sql: string;
  type: "string" | "time" | "number" | "boolean";
  dim1Field: string | null;
  dim2Field: string | null;
  description?: string;
}

export type MetricKind = "discrete" | "continuous";

export interface MeasureDef {
  id: string;
  label: string;
  sql: string;
  agg: Agg;
  kind: MetricKind;
  unit?: Unit;
  format?: string;
  filters?: Record<string, unknown>[];
  dependsOn?: string[];
  description?: string;
  isCumulative?: boolean;
  normalizeBy?: string;
  owner?: string;
  tags?: string[];
  meanExpr?: string;
  stdExpr?: string;
  normalizeExpr?: string;
  /** Restrict which dimensions can be used for filtering this metric. If not specified, all dimensions are available. */
  filterDimensions?: string[];
}

export interface MetricModel {
  namespace: string;
  dimensions: Record<string, DimensionDef>;
  measures: Record<string, MeasureDef>;
}

const dimensions: Record<string, DimensionDef> = {
  all: {
    id: "all",
    label: "All",
    sql: "NULL",
    type: "string",
    dim1Field: null,
    dim2Field: null,
    description: "Aggregated across all records",
  },
  organization: {
    id: "organization",
    label: "Organization",
    sql: "organization",
    type: "string",
    dim1Field: "organization",
    dim2Field: null,
    description: "todo:missing",
  },
  campaign_type: {
    id: "campaign_type",
    label: "Campaign Type",
    sql: "campaign_type",
    type: "string",
    dim1Field: "campaign_type",
    dim2Field: null,
    description: "todo:missing",
  },
  placement_type: {
    id: "placement_type",
    label: "Placement Type",
    sql: "placement_type",
    type: "string",
    dim1Field: "placement_type",
    dim2Field: null,
    description: "todo:missing",
  },
  ad_type: {
    id: "ad_type",
    label: "Ad Type",
    sql: "ad_type",
    type: "string",
    dim1Field: "ad_type",
    dim2Field: null,
    description: "todo:missing",
  },
  audience: {
    id: "audience",
    label: "Audience",
    sql: "audience",
    type: "string",
    dim1Field: "audience",
    dim2Field: null,
    description: "todo:missing",
  },
  country: {
    id: "country",
    label: "Country",
    sql: "country",
    type: "string",
    dim1Field: "country",
    dim2Field: null,
    description: "todo:missing",
  },
  partner: {
    id: "partner",
    label: "Partner",
    sql: "partner",
    type: "string",
    dim1Field: "partner",
    dim2Field: null,
    description: "todo:missing",
  },
  supply_type: {
    id: "supply_type",
    label: "Supply Type",
    sql: "supply_type",
    type: "string",
    dim1Field: "supply_type",
    dim2Field: null,
    description: "todo:missing",
  },
  primary_prediction_flight_id: {
    id: "primary_prediction_flight_id",
    label: "Primary Prediction Flight ID",
    sql: "primary_prediction_flight_id",
    type: "string",
    dim1Field: "primary_prediction_flight_id",
    dim2Field: null,
    description: "todo:missing",
  },
  budget_id: {
    id: "budget_id",
    label: "Budget ID",
    sql: "budget_id",
    type: "string",
    dim1Field: "budget_id",
    dim2Field: null,
    description: "todo:missing",
  },
};

const measures: Record<string, MeasureDef> = {
  //bid_opportunities: {
  //  id: "bid_opportunities",
  //  label: "Bid Opportunities",
  //  sql: "bid_opportunities_sum",
  //  agg: "sum",
  //  kind: "discrete",
  //  unit: "count",
  //  isCumulative: true,
  //  description: "Bid Requests that matched the user identifier of at least one audience",
  //  owner: "Johannes",
  //},
  bids: {
    id: "bids",
    label: "Bids",
    description: "Sum of bids that Remerge placed.",
    sql: "bids_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "Dmitry",
    tags: ["Operations"],
  },
  impressions: {
    id: "impressions",
    label: "Impressions",
    description: "How often an ad was shown to a user.",
    sql: "impressions_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "Dmitry",
    tags: ["Operations"],
  },
  clicks: {
    id: "clicks",
    label: "Clicks",
    description: "Sum of clicks generated by users.",
    sql: "clicks_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "Dmitry",
    tags: ["Operations"],
  },
  //bid_rate: {
  //  id: "bid_rate",
  //  label: "Bid Rate",
  //  description: "Ratio of bids to bid opportunities.",
  //  sql: "bids_sum / nullif(bid_opportunities_sum, 0)",
  //  agg: "none",
  //  kind: "discrete",
  //  unit: "ratio",
  //  format: "0.00%",
  //  owner: "PERF",
  //},
  ctr: {
    id: "ctr",
    label: "CTR",
    sql: "clicks_sum / NULLIF(impressions_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description:
      "Observed click-through rate (clicks / impressions).\n\nWhy: the value the CTR model is trying to predict.\nInterpretation: higher = better (more engagement per impression). No universal target — depends on audience mix and user behaviour; watch for breaks in the trend, not absolute values.",
    owner: "Matthias",
    tags: ["Operations"],
  },
  ctr_calibration_log_ratio: {
    id: "ctr_calibration_log_ratio",
    label: "CTR log ratio",
    sql: "CASE WHEN clicks_sum > 0 AND impressions_sum > 0 AND estimated_impression_ctr_mean > 0 THEN LN(CAST(clicks_sum AS DOUBLE) / impressions_sum) - LN(estimated_impression_ctr_mean) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00",
    description:
      "Log ratio of observed CTR to model-predicted CTR. Direction and magnitude of model bias.\n\nWhy: tells you whether the CTR model is systematically over- or under-predicting, scale-free.\nInterpretation:\n- 0 → perfectly calibrated (good)\n- negative → model over-predicts (impression value gets inflated)\n- positive → model under-predicts (impression value gets deflated)\nSymmetric magnitude: ±0.1 ≈ 10% off, ±0.7 ≈ 2× off, ±2.3 ≈ 10× off.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  reengagement_calibration_log_ratio_1d: {
    id: "reengagement_calibration_log_ratio_1d",
    label: "Reengagement log ratio 1d",
    sql: "CASE WHEN reengagements_1d_sum > 0 AND impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 THEN LN(CAST(reengagements_1d_sum AS DOUBLE) / impressions_sum) - LN(estimated_impression_reengagement_rate_mean) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00",
    description:
      "Log ratio of observed 1d self-attributed reengagement rate to the model's predicted rate.\n\nWhy: leading indicator — predictor targets 7d, so this sits negative by design. Track movement in the gap, not the zero line.\nInterpretation: good = stable around its baseline; bad = drift in either direction (bias has shifted).",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  reengagement_calibration_log_ratio_7d: {
    id: "reengagement_calibration_log_ratio_7d",
    label: "Reengagement log ratio 7d",
    sql: "CASE WHEN reengagements_7d_sum > 0 AND impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 THEN LN(CAST(reengagements_7d_sum AS DOUBLE) / impressions_sum) - LN(estimated_impression_reengagement_rate_mean) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00",
    description:
      "Log ratio of observed 7d self-attributed reengagement rate to the model's predicted rate.\n\nWhy: scale-free check on whether the model systematically over- or under-predicts.\nInterpretation:\n- 0 → calibrated (good)\n- negative → over-predicting (impression value gets inflated)\n- positive → under-predicting (impression value gets deflated)\nRightmost 7 days still maturing and will read artificially negative.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  ap_reengagement_calibration_log_ratio_1d: {
    id: "ap_reengagement_calibration_log_ratio_1d",
    label: "AP Reengagement log ratio 1d",
    sql: "CASE WHEN ap_reengagements_1d_sum > 0 AND impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 THEN LN(CAST(ap_reengagements_1d_sum AS DOUBLE) / impressions_sum) - LN(estimated_impression_ap_reengagement_rate_mean) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00",
    description:
      "Log ratio of observed 1d MMP-attributed reengagement rate to the model's predicted rate.\n\nWhy: leading indicator — predictor targets 7d, so this sits negative by design. Track movement in the gap, not the zero line.\nInterpretation: good = stable around its baseline; bad = drift in either direction (bias has shifted).",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  ap_reengagement_calibration_log_ratio_7d: {
    id: "ap_reengagement_calibration_log_ratio_7d",
    label: "AP Reengagement log ratio 7d",
    sql: "CASE WHEN ap_reengagements_7d_sum > 0 AND impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 THEN LN(CAST(ap_reengagements_7d_sum AS DOUBLE) / impressions_sum) - LN(estimated_impression_ap_reengagement_rate_mean) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00",
    description:
      "Log ratio of observed 7d MMP-attributed reengagement rate to the model's predicted rate.\n\nWhy: scale-free check on whether the model systematically over- or under-predicts.\nInterpretation:\n- 0 → calibrated (good)\n- negative → over-predicting (impression value gets inflated)\n- positive → under-predicting (impression value gets deflated)\nRightmost 7 days still maturing and will read artificially negative.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  impression_rate: {
    id: "impression_rate",
    label: "Impression Rate",
    description: "Average ratio of impressions vs wins (impressions / wins).",
    sql: "impressions_sum / NULLIF(wins_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "David",
    tags: ["Operations"],
  },
  win_rate: {
    id: "win_rate",
    label: "Win Rate",
    description: "Average ratio of wins vs bids (wins / bids).",
    sql: "wins_sum / NULLIF(bids_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Alfred",
    tags: ["Operations"],
  },
  // total_budget_eur: {
  // id: "total_budget_eur",
  // label: "Total Budget (EUR)",
  // sql: "total_budget_eur / 1000000",
  // agg: "sum",
  // kind: "discrete",
  // unit: "EUR",
  // format: "€0,0.00",
  // isCumulative: true,
  // description:
  // "Budget available in OLIs for active campaigns to be charged to clients. Business growth on existing and new clients.",
  // owner: "Johannes",
  // },
  // used_budget_eur: {
  // id: "used_budget_eur",
  // label: "Used Budget (EUR)",
  // sql: "used_budget_eur / 1000000",
  // agg: "sum",
  // kind: "discrete",
  // unit: "EUR",
  // format: "€0,0.00",
  // isCumulative: true,
  // description:
  // "Cost charged to client. Realization of our potential scale. Failure to spend leads to less available budget.",
  // owner: "Johannes",
  // },
  // budget_utilization: {
  // id: "budget_utilization",
  // label: "Budget Utilization",
  // sql: "used_budget_eur / NULLIF(total_budget_eur, 0)",
  // agg: "none",
  // kind: "discrete",
  // unit: "ratio",
  // format: "0.0%",
  // description:
  // "Share of available budget that is currently used (used / total).",
  // owner: "PERF",
  // },
  revenue_eur: {
    id: "revenue_eur",
    label: "Revenue (EUR)",
    description: "Remerge revenue (client cost) in EUR.",
    sql: "buying_price_eur_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    owner: "Freddie",
    tags: ["Operations"],
  },
  impression_value_over_supply_cost: {
    id: "impression_value_over_supply_cost",
    label: "Impression Value over Supply Cost",
    description:
      "Predicted return on supply cost, similar to ROAS, but using our own valuation instead of waiting for actual AP revenue or conversions.  Higher values indicate better pricing efficiency, break-even is 1.",
    sql: "(impression_value_impressions_sum / (1000*1000000) ) / NULLIF((win_price_eur_sum / 1000000), 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Alfred",
    tags: ["Operations"],
  },
  ap_event_value_d1: {
    id: "ap_event_value_d1",
    label: "AP Event Value D1 (EUR)",
    description:
      "Total AP value generated by the bidder for clients within 1 day of bid. Temporary metric to validate ap_event_value_eur data while D30 accumulates.",
    sql: "ap_event_value_eur_1d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    owner: "Johannes",
    tags: ["Operations"],
  },
  ap_event_value_d30: {
    id: "ap_event_value_d30",
    label: "AP Event Value D30 (EUR)",
    description:
      "Total AP value generated by the bidder for clients within 30 days of bid (sum of ap_event_value_eur for conversion and revenue events). In EUR.",
    sql: "ap_event_value_eur_30d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    owner: "Johannes",
    tags: ["Operations"],
  },
  ap_event_value_d30_over_supply_cost: {
    id: "ap_event_value_d30_over_supply_cost",
    label: "AP Event Value D30 / Supply Cost",
    description:
      "Efficiency of the bidder: actual € value generated per € supply cost within 30 days. Higher is better, break-even is 1.",
    sql: "ap_event_value_eur_30d_sum / NULLIF(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Johannes",
    tags: ["Operations"],
  },
  conversions_1d: {
    id: "conversions_1d",
    label: "Conversion 1d",
    sql: "conversions_1d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 1 day of bid (self-attributed by remerge)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  ap_conversions_1d: {
    id: "ap_conversions_1d",
    label: "AP Conversion 1d",
    sql: "ap_conversions_1d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 1 day of bid (MMP-attributed)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  mmp_attribution_ratio_1d: {
    id: "mmp_attribution_ratio_1d",
    label: "MMP Attribution Ratio 1D",
    description: "Ratio of MMP-attributed conversions to all conversions.",
    sql: "ap_conversions_1d_sum / nullif(conversions_1d_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  customer_revenue_1d: {
    id: "customer_revenue_1d",
    label: "Customer Revenue 1d (EUR)",
    sql: "customer_revenue_eur_1d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 1 day of bid (self-attributed by remerge) in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_customer_revenue_1d: {
    id: "ap_customer_revenue_1d",
    label: "AP Customer Revenue 1d (EUR)",
    sql: "ap_customer_revenue_eur_1d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 1 day of bid (MMP-attributed) in €",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  supply_ecpx_1d: {
    id: "supply_ecpx_1d",
    label: "Supply eCPX 1d (EUR)",
    sql: "win_price_eur_sum / NULLIF(conversions_1d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description:
      "Avg. cost to remerge per self-attributed conversion within 1 day in €. Measures how efficiently we produce conversions, ignoring lost attributions to competitors or MMP rules",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_ecpx_1d: {
    id: "supply_ap_ecpx_1d",
    label: "Supply AP eCPX 1d (EUR)",
    sql: "win_price_eur_sum / NULLIF(ap_conversions_1d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description:
      "Avg. cost to remerge per MMP-attributed conversion within 1 day in €. Measures how efficiently we produce conversions, not including attributions lost to competitors or excluded by MMP rules",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_roas_1d: {
    id: "supply_ap_roas_1d",
    label: "Supply AP ROAS 1d",
    sql: "ap_customer_revenue_eur_1d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 1 day (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_roas_1d: {
    id: "supply_roas_1d",
    label: "Supply ROAS 1d",
    sql: "customer_revenue_eur_1d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 1 day of bid (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  roas_1d: {
    id: "roas_1d",
    label: "ROAS 1d",
    sql: "customer_revenue_eur_1d_sum / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 1 day of bid (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_roas_1d: {
    id: "ap_roas_1d",
    label: "AP ROAS 1d",
    sql: "ap_customer_revenue_eur_1d_sum /  nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 1 day (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  eRPM_1d: {
    id: "eRPM_1d",
    label: "eRPM 1D",
    sql: "(customer_revenue_eur_1d_sum / nullif(impressions_sum, 0) / 1000000) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR/1kImpressions",
    format: "€0,0.00",
    description: "Customer revenue per 1000 impressions within 1 day in € (self-attributed)",
    owner: "Martin K",
    tags: ["Advertising Performance"],
  },
  conversions_3d: {
    id: "conversions_3d",
    label: "Conversion 3d",
    sql: "conversions_3d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 3 days of bid (self-attributed by remerge)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_conversions_3d: {
    id: "ap_conversions_3d",
    label: "AP Conversion 3d",
    sql: "ap_conversions_3d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 3 days of bid (MMP-attributed)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  mmp_attribution_ratio_3d: {
    id: "mmp_attribution_ratio_3d",
    label: "MMP Attribution Ratio 3D",
    description: "Ratio of MMP-attributed conversions to all conversions.",
    sql: "ap_conversions_3d_sum / nullif(conversions_3d_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  customer_revenue_3d: {
    id: "customer_revenue_3d",
    label: "Customer Revenue 3d (EUR)",
    sql: "customer_revenue_eur_3d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 3 days of bid (self-attributed by remerge) in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_customer_revenue_3d: {
    id: "ap_customer_revenue_3d",
    label: "AP Customer Revenue 3d (EUR)",
    sql: "ap_customer_revenue_eur_3d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 3 days of bid (MMP-attributed) in €",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  supply_ecpx_3d: {
    id: "supply_ecpx_3d",
    label: "Supply eCPX 3d (EUR)",
    sql: "win_price_eur_sum / NULLIF(conversions_3d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per self-attributed conversion within 3 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_ecpx_3d: {
    id: "supply_ap_ecpx_3d",
    label: "Supply AP eCPX 3d (EUR)",
    sql: "win_price_eur_sum / NULLIF(ap_conversions_3d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per MMP-attributed conversion within 3 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_roas_3d: {
    id: "supply_ap_roas_3d",
    label: "Supply AP ROAS 3d",
    sql: "ap_customer_revenue_eur_3d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 3 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_roas_3d: {
    id: "supply_roas_3d",
    label: "Supply ROAS 3d",
    sql: "customer_revenue_eur_3d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 3 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  roas_3d: {
    id: "roas_3d",
    label: "ROAS 3d",
    sql: "customer_revenue_eur_3d_sum / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 3 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_roas_3d: {
    id: "ap_roas_3d",
    label: "AP ROAS 3d",
    sql: "ap_customer_revenue_eur_3d_sum /  nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 3 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  eRPM_3d: {
    id: "eRPM_3d",
    label: "eRPM 3D",
    sql: "(customer_revenue_eur_3d_sum / nullif(impressions_sum, 0) / 1000000) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR/1kImpressions",
    format: "€0,0.00",
    description: "Customer revenue per 1000 impressions within 3 days in € (self-attributed)",
    owner: "Martin K",
    tags: ["Advertising Performance"],
  },
  conversions_7d: {
    id: "conversions_7d",
    label: "Conversion 7d",
    sql: "conversions_7d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 7 days of bid (self-attributed by remerge)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_conversions_7d: {
    id: "ap_conversions_7d",
    label: "AP Conversion 7d",
    sql: "ap_conversions_7d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 7 days of bid (MMP-attributed)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  mmp_attribution_ratio_7d: {
    id: "mmp_attribution_ratio_7d",
    label: "MMP Attribution Ratio 7D",
    description: "Ratio of MMP-attributed conversions to all conversions.",
    sql: "ap_conversions_7d_sum / nullif(conversions_7d_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  customer_revenue_7d: {
    id: "customer_revenue_7d",
    label: "Customer Revenue 7d (EUR)",
    sql: "customer_revenue_eur_7d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 7 days of bid (self-attributed by remerge) in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_customer_revenue_7d: {
    id: "ap_customer_revenue_7d",
    label: "AP Customer Revenue 7d (EUR)",
    sql: "ap_customer_revenue_eur_7d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 7 days of bid (MMP-attributed) in €",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  supply_ecpx_7d: {
    id: "supply_ecpx_7d",
    label: "Supply eCPX 7d (EUR)",
    sql: "win_price_eur_sum / NULLIF(conversions_7d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per self-attributed conversion within 7 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_ecpx_7d: {
    id: "supply_ap_ecpx_7d",
    label: "Supply AP eCPX 7d (EUR)",
    sql: "win_price_eur_sum / NULLIF(ap_conversions_7d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per MMP-attributed conversion within 7 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_roas_7d: {
    id: "supply_ap_roas_7d",
    label: "Supply AP ROAS 7d",
    sql: "ap_customer_revenue_eur_7d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 7 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_roas_7d: {
    id: "supply_roas_7d",
    label: "Supply ROAS 7d",
    sql: "customer_revenue_eur_7d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 7 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  roas_7d: {
    id: "roas_7d",
    label: "ROAS 7d",
    sql: "customer_revenue_eur_7d_sum / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 7 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_roas_7d: {
    id: "ap_roas_7d",
    label: "AP ROAS 7d",
    sql: "ap_customer_revenue_eur_7d_sum /  nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 7 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  eRPM_7d: {
    id: "eRPM_7d",
    label: "eRPM 7D",
    sql: "(customer_revenue_eur_7d_sum / nullif(impressions_sum, 0) / 1000000) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR/1kImpressions",
    format: "€0,0.00",
    description: "Customer revenue per 1000 impressions within 7 days in € (self-attributed)",
    owner: "Martin K",
    tags: ["Advertising Performance"],
  },
  conversions_14d: {
    id: "conversions_14d",
    label: "Conversion 14d",
    sql: "conversions_14d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 14 days of bid (self-attributed by remerge)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_conversions_14d: {
    id: "ap_conversions_14d",
    label: "AP Conversion 14d",
    sql: "ap_conversions_14d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 14 days of bid (MMP-attributed)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  mmp_attribution_ratio_14d: {
    id: "mmp_attribution_ratio_14d",
    label: "MMP Attribution Ratio 14D",
    description: "Ratio of MMP-attributed conversions to all conversions.",
    sql: "ap_conversions_14d_sum / nullif(conversions_14d_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  customer_revenue_14d: {
    id: "customer_revenue_14d",
    label: "Customer Revenue 14d (EUR)",
    sql: "customer_revenue_eur_14d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 14 days of bid (self-attributed by remerge) in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_customer_revenue_14d: {
    id: "ap_customer_revenue_14d",
    label: "AP Customer Revenue 14d (EUR)",
    sql: "ap_customer_revenue_eur_14d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 14 days of bid (MMP-attributed) in €",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  supply_ecpx_14d: {
    id: "supply_ecpx_14d",
    label: "Supply eCPX 14d (EUR)",
    sql: "win_price_eur_sum / NULLIF(conversions_14d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per self-attributed conversion within 14 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_ecpx_14d: {
    id: "supply_ap_ecpx_14d",
    label: "Supply AP eCPX 14d (EUR)",
    sql: "win_price_eur_sum / NULLIF(ap_conversions_14d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per MMP-attributed conversion within 14 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_roas_14d: {
    id: "supply_ap_roas_14d",
    label: "Supply AP ROAS 14d",
    sql: "ap_customer_revenue_eur_14d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 14 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_roas_14d: {
    id: "supply_roas_14d",
    label: "Supply ROAS 14d",
    sql: "customer_revenue_eur_14d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 14 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  roas_14d: {
    id: "roas_14d",
    label: "ROAS 14d",
    sql: "customer_revenue_eur_14d_sum / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 14 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_roas_14d: {
    id: "ap_roas_14d",
    label: "AP ROAS 14d",
    sql: "ap_customer_revenue_eur_14d_sum /  nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 14 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  eRPM_14d: {
    id: "eRPM_14d",
    label: "eRPM 14D",
    sql: "(customer_revenue_eur_14d_sum / nullif(impressions_sum, 0) / 1000000) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR/1kImpressions",
    format: "€0,0.00",
    description: "Customer revenue per 1000 impressions within 14 days in € (self-attributed)",
    owner: "Martin K",
    tags: ["Advertising Performance"],
  },
  conversions_30d: {
    id: "conversions_30d",
    label: "Conversion 30d",
    sql: "conversions_30d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 30 days of bid (self-attributed by remerge)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_conversions_30d: {
    id: "ap_conversions_30d",
    label: "AP Conversion 30d",
    sql: "ap_conversions_30d_sum",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description: "Conversions within 30 days of bid (MMP-attributed)",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  mmp_attribution_ratio_30d: {
    id: "mmp_attribution_ratio_30d",
    label: "MMP Attribution Ratio 30D",
    description: "Ratio of MMP-attributed conversions to all conversions.",
    sql: "ap_conversions_30d_sum / nullif(conversions_30d_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  customer_revenue_30d: {
    id: "customer_revenue_30d",
    label: "Customer Revenue 30d (EUR)",
    sql: "customer_revenue_eur_30d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 30 days of bid (self-attributed by remerge) in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_customer_revenue_30d: {
    id: "ap_customer_revenue_30d",
    label: "AP Customer Revenue 30d (EUR)",
    sql: "ap_customer_revenue_eur_30d_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    description: "In-app revenue within 30 days of bid (MMP-attributed) in €",
    owner: "Saranya",
    tags: ["Advertising Performance"],
  },
  supply_ecpx_30d: {
    id: "supply_ecpx_30d",
    label: "Supply eCPX 30d (EUR)",
    sql: "win_price_eur_sum / NULLIF(conversions_30d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per self-attributed conversion within 30 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_ecpx_30d: {
    id: "supply_ap_ecpx_30d",
    label: "Supply AP eCPX 30d (EUR)",
    sql: "win_price_eur_sum / NULLIF(ap_conversions_30d_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per MMP-attributed conversion within 30 days in €",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_ap_roas_30d: {
    id: "supply_ap_roas_30d",
    label: "Supply AP ROAS 30d",
    sql: "ap_customer_revenue_eur_30d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 30 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  supply_roas_30d: {
    id: "supply_roas_30d",
    label: "Supply ROAS 30d",
    sql: "customer_revenue_eur_30d_sum /  nullif(win_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. remerge cost within 30 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  roas_30d: {
    id: "roas_30d",
    label: "ROAS 30d",
    sql: "customer_revenue_eur_30d_sum / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 30 days (self-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  ap_roas_30d: {
    id: "ap_roas_30d",
    label: "AP ROAS 30d",
    sql: "ap_customer_revenue_eur_30d_sum /  nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "In-app revenue vs. client cost within 30 days (MMP-attributed)",
    owner: "Freddie",
    tags: ["Advertising Performance"],
  },
  eRPM_30d: {
    id: "eRPM_30d",
    label: "eRPM 30D",
    sql: "(customer_revenue_eur_30d_sum / nullif(impressions_sum, 0) / 1000000) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR/1kImpressions",
    format: "€0,0.00",
    description: "Customer revenue per 1000 impressions within 30 days in € (self-attributed)",
    owner: "Martin K",
    tags: ["Advertising Performance"],
  },
  // ───── Reengagement volume + observed rate (parallel to conversions + CTR) ─────
  reengagements_1d: {
    id: "reengagements_1d",
    label: "Reengagement 1d",
    sql: "CASE WHEN estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN reengagements_1d_sum ELSE NULL END",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description:
      "Reengagements within 1 day of the bid, self-attributed by remerge.\n\nWhy: leading indicator for the 7d label the model targets.\nInterpretation: higher = better. No universal target — watch trend breaks, not absolute counts.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  reengagements_7d: {
    id: "reengagements_7d",
    label: "Reengagement 7d",
    sql: "CASE WHEN estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN reengagements_7d_sum ELSE NULL END",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description:
      "Reengagements within 7 days of the bid, self-attributed by remerge.\n\nWhy: matches the reengagement model's training target.\nInterpretation: higher = better. No universal target — watch trend breaks. Rightmost 7 days are still filling in.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  ap_reengagements_1d: {
    id: "ap_reengagements_1d",
    label: "AP Reengagement 1d",
    sql: "CASE WHEN estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN ap_reengagements_1d_sum ELSE NULL END",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description:
      "Reengagements within 1 day of the bid, attributed by the partner MMP.\n\nWhy: leading indicator for the 7d label the model targets.\nInterpretation: higher = better. No universal target — watch trend breaks, not absolute counts.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  ap_reengagements_7d: {
    id: "ap_reengagements_7d",
    label: "AP Reengagement 7d",
    sql: "CASE WHEN estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN ap_reengagements_7d_sum ELSE NULL END",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    description:
      "Reengagements within 7 days of the bid, attributed by the partner MMP.\n\nWhy: matches the reengagement model's training target.\nInterpretation: higher = better. No universal target — watch trend breaks. Rightmost 7 days are still filling in.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  reengagement_rate_1d: {
    id: "reengagement_rate_1d",
    label: "Reengagement Rate 1d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN reengagements_1d_sum / impressions_sum ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description:
      "Self-attributed 1d reengagement rate (reengagements / impressions).\n\nWhy: leading indicator for the 7d rate. Predictor targets 7d, so 1d sits below prediction by design.\nInterpretation: higher = better. No universal target — watch trend breaks.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  reengagement_rate_7d: {
    id: "reengagement_rate_7d",
    label: "Reengagement Rate 7d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN reengagements_7d_sum / impressions_sum ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description:
      "Self-attributed 7d reengagement rate (reengagements / impressions).\n\nWhy: the value the reengagement model predicts.\nInterpretation: higher = better. No universal target — watch trend breaks. Rightmost 7 days are still filling in.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  ap_reengagement_rate_1d: {
    id: "ap_reengagement_rate_1d",
    label: "AP Reengagement Rate 1d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN ap_reengagements_1d_sum / impressions_sum ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description:
      "MMP-attributed 1d reengagement rate (ap_reengagements / impressions).\n\nWhy: leading indicator for the 7d rate. Predictor targets 7d, so 1d sits below prediction by design.\nInterpretation: higher = better. No universal target — watch trend breaks.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  ap_reengagement_rate_7d: {
    id: "ap_reengagement_rate_7d",
    label: "AP Reengagement Rate 7d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN ap_reengagements_7d_sum / impressions_sum ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description:
      "MMP-attributed 7d reengagement rate (ap_reengagements / impressions).\n\nWhy: the value the reengagement model predicts.\nInterpretation: higher = better. No universal target — watch trend breaks. Rightmost 7 days are still filling in.",
    owner: "Matthias",
    tags: ["Advertising Performance"],
  },
  margin: {
    id: "margin",
    label: "Margin",
    description: "Remerge margin percentage (revenue minus supply cost, divided by revenue).",
    sql: "(buying_price_eur_sum - win_price_eur_sum) / nullif(buying_price_eur_sum, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Freddie",
    tags: ["Operations", "Advertising Performance"],
  },
  profit_eur: {
    id: "profit_eur",
    label: "Profit (EUR)",
    description: "Remerge profit in EUR (revenue minus supply cost).",
    sql: "(buying_price_eur_sum - win_price_eur_sum) / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    isCumulative: true,
    owner: "Freddie",
    tags: ["Operations", "Advertising Performance"],
  },
  supply_ecpc: {
    id: "supply_ecpc",
    label: "Supply eCPC (EUR)",
    sql: "win_price_eur_sum /  nullif(clicks_sum, 0) / 1000000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost to remerge per click in €",
    owner: "Freddie",
    tags: ["Operations"],
  },
  impression_ctr_model_logloss: {
    id: "impression_ctr_model_logloss",
    label: "CTR log-loss",
    sql: "-impression_ctr_model_logloss", // TBD: instead add the - missing in data getter
    agg: "avg",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Per-impression cross-entropy of the CTR model's predictions vs. actual clicks.\n\nWhy: a single scalar capturing both calibration and ranking; penalises overconfident wrong predictions in either direction. Use the log-ratio panel for bias direction and the by-audience boxplot to localise.\nInterpretation: lower is better; no upper bound. 0 is perfect (unreachable). Watch the level over time, not absolute values.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  impression_reengagement_model_logloss_1d: {
    id: "impression_reengagement_model_logloss_1d",
    label: "Reengagement log-loss 1d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN -((reengagements_1d_sum / impressions_sum) * LN(GREATEST(LEAST(estimated_impression_reengagement_rate_mean, 1 - 1e-6), 1e-6)) + (1 - reengagements_1d_sum / impressions_sum) * LN(1 - GREATEST(LEAST(estimated_impression_reengagement_rate_mean, 1 - 1e-6), 1e-6))) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Cross-entropy of the reengagement model's predictions vs. observed 1d self-attributed reengagements.\n\nWhy: single scalar capturing calibration and ranking; use the log-ratio panel for bias direction.\nInterpretation: lower = better. No upper bound — watch level over time.\n\nCaveat: computed from daily observed and average predicted rate, not per impression. Approximate.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  impression_reengagement_model_logloss_7d: {
    id: "impression_reengagement_model_logloss_7d",
    label: "Reengagement log-loss 7d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_reengagement_rate_mean) THEN -((reengagements_7d_sum / impressions_sum) * LN(GREATEST(LEAST(estimated_impression_reengagement_rate_mean, 1 - 1e-6), 1e-6)) + (1 - reengagements_7d_sum / impressions_sum) * LN(1 - GREATEST(LEAST(estimated_impression_reengagement_rate_mean, 1 - 1e-6), 1e-6))) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Cross-entropy of the reengagement model's predictions vs. observed 7d self-attributed reengagements.\n\nWhy: single scalar capturing calibration and ranking; penalises overconfident wrong predictions.\nInterpretation: lower = better. No upper bound — watch level over time. Rightmost 7 days still maturing.\n\nCaveat: computed from daily observed and average predicted rate, not per impression. Approximate.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  ap_impression_reengagement_model_logloss_1d: {
    id: "ap_impression_reengagement_model_logloss_1d",
    label: "AP Reengagement log-loss 1d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN -((ap_reengagements_1d_sum / impressions_sum) * LN(GREATEST(LEAST(estimated_impression_ap_reengagement_rate_mean, 1 - 1e-6), 1e-6)) + (1 - ap_reengagements_1d_sum / impressions_sum) * LN(1 - GREATEST(LEAST(estimated_impression_ap_reengagement_rate_mean, 1 - 1e-6), 1e-6))) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Cross-entropy of the reengagement model's predictions vs. observed 1d MMP-attributed reengagements.\n\nWhy: single scalar capturing calibration and ranking; use the log-ratio panel for bias direction.\nInterpretation: lower = better. No upper bound — watch level over time.\n\nCaveat: computed from daily observed and average predicted rate, not per impression. Approximate.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  ap_impression_reengagement_model_logloss_7d: {
    id: "ap_impression_reengagement_model_logloss_7d",
    label: "AP Reengagement log-loss 7d",
    sql: "CASE WHEN impressions_sum > 0 AND estimated_impression_ap_reengagement_rate_mean > 0 AND NOT isnan(estimated_impression_ap_reengagement_rate_mean) THEN -((ap_reengagements_7d_sum / impressions_sum) * LN(GREATEST(LEAST(estimated_impression_ap_reengagement_rate_mean, 1 - 1e-6), 1e-6)) + (1 - ap_reengagements_7d_sum / impressions_sum) * LN(1 - GREATEST(LEAST(estimated_impression_ap_reengagement_rate_mean, 1 - 1e-6), 1e-6))) ELSE NULL END",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Cross-entropy of the reengagement model's predictions vs. observed 7d MMP-attributed reengagements.\n\nWhy: single scalar capturing calibration and ranking; penalises overconfident wrong predictions.\nInterpretation: lower = better. No upper bound — watch level over time. Rightmost 7 days still maturing.\n\nCaveat: computed from daily observed and average predicted rate, not per impression. Approximate.",
    owner: "Matthias",
    tags: ["Bidding Logic"],
  },
  win_rate_mse: {
    id: "win_rate_mse",
    label: "Win Rate MSE",
    sql: "win_rate_mse",
    agg: "avg",
    kind: "discrete",
    unit: "ratio",
    format: "0.0000",
    description:
      "Mean squared error of win rate predictions. (Not including bids where bid price was selected by heuristic without the win rate)",
    owner: "Piyush",
    tags: ["Bidding Logic"],
  },
  reason_unused_bid_too_low_share: {
    id: "reason_unused_bid_too_low_share",
    label: "Share floor price fallback",
    sql: "reason_unused_bid_too_low_share",
    agg: "avg",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "Share of bids that fell back to floor price (bid too low)",
    owner: "Piyush",
    tags: ["Bidding Logic"],
  },
  reason_unused_bid_too_high_share: {
    id: "reason_unused_bid_too_high_share",
    label: "Share Max Bid Fallback",
    sql: "reason_unused_bid_too_high_share",
    agg: "avg",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    description: "Share of bids that fell back to max bid limit (bid too high)",
    owner: "Piyush",
    tags: ["Bidding Logic"],
  },
  avg_bid_price: {
    id: "avg_bid_price",
    label: "Avg. Bid Price CPM (EUR)",
    description: "Average value of placed bids (CPM).",
    sql: "bid_price_eur",
    agg: "avg",
    kind: "continuous",
    unit: "EUR",
    format: "€0,0.00",
    normalizeBy: "bids_sum",
    owner: "Johannes",
    tags: ["Bidding Logic"],
    meanExpr: "bid_price_eur_mean / 1000000",
    stdExpr: "bid_price_eur_sd / 1000000",
  },
  campaign_cpc: {
    id: "campaign_cpc",
    label: "Campaign CPC (EUR)",
    description: "CPC configured at campaign level (autospend objective may adjust actual CPC).",
    sql: "campaign_cpc_eur",
    agg: "avg",
    kind: "continuous",
    unit: "EUR",
    format: "€0,0.00",
    normalizeBy: "bids_sum",
    owner: "Johannes",
    tags: ["Bidding Logic"],
    meanExpr: "campaign_cpc_eur_mean / 1000000",
    stdExpr: "campaign_cpc_eur_sd / 1000000",
  },
  supply_cpm_eur: {
    id: "supply_cpm_eur",
    label: "Supply Cost Per Mille (CPM) (EUR)",
    sql: "(win_price_eur_sum / 1000000) / NULLIF(impressions_sum, 0) * 1000",
    agg: "none",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Avg. cost paid per 1000 impressions in €",
    owner: "Johannes",
    tags: ["Operations"],
  },
  supply_cost_eur: {
    id: "supply_cost_eur",
    label: "Supply Cost (EUR)",
    sql: "win_price_eur_sum / 1000000",
    agg: "sum",
    kind: "discrete",
    unit: "EUR",
    format: "€0,0.00",
    description: "Total cost to remerge in €",
    owner: "Freddie",
    tags: ["Operations"],
  },
  impression_value: {
    id: "impression_value",
    label: "Impression Value (CPM)",
    description:
      "Estimated impression value for 1000 impressions (CPM) to the advertiser after applying bid multipliers and CTR predictions.",
    sql: "impression_value",
    agg: "avg",
    kind: "continuous",
    unit: "EUR",
    format: "€0,0.00",
    normalizeBy: "bids_sum",
    owner: "Johannes",
    tags: ["Bidding Logic"],
    meanExpr: "impression_value_mean / 1000000",
    stdExpr: "impression_value_sd / 1000000",
  },
  spend_adjustment: {
    id: "spend_adjustment",
    label: "Spend Adjustment Multiplier",
    description:
      "Multiplier (0-1) that reduces max bid according to the current budget pacing. Reduces the bid price when we're spending easily which implies many bid opportunities are available and thus low opportunity cost of not winning this impression.",
    sql: "spend_adjustment",
    agg: "avg",
    kind: "continuous",
    unit: "ratio",
    format: "0.00",
    owner: "Ahmad",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    meanExpr: "spend_adjustment_mean",
    stdExpr: "spend_adjustment_sd",
  },
  auto_spend_adjustment: {
    id: "auto_spend_adjustment",
    label: "Auto Spend Adjustment",
    description:
      "Adjustment multiplier on admin CPC produced by the Auto-Spend model to control pacing. It is averaged over the number of bids over the selected slice.",
    sql: "auto_spend_adjustment",
    agg: "avg",
    kind: "continuous",
    unit: "ratio",
    format: "0.00",
    owner: "Ahmad",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    meanExpr: "auto_spend_adjustment_mean",
    stdExpr: "auto_spend_adjustment_sd",
  },
  pacing_adjustment: {
    id: "pacing_adjustment",
    label: "Pacing Adjustment Multiplier",
    description:
      "Multiplier on impression value that produces max bid (maxBid = impressionValue * pacingAdjustment). Conceptually equals 1/lambda, the marginal opportunity cost of budget — higher = more aggressive bidding, lower = tighter implied ROAS floor. See the Bidding Algorithm Conceptual Framework page.",
    sql: "pacing_adjustment",
    agg: "avg",
    kind: "continuous",
    format: "0.00",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    meanExpr: "pacing_adjustment_mean",
    stdExpr: "pacing_adjustment_sd",
  },
  selectivity: {
    id: "selectivity",
    label: "Selectivity",
    description:
      "Supply-weighted geometric mean of 1/pacing_adjustment, i.e. exp(Σ supply_i · log(1/pacingAdj_i) / Σ supply_i) where supply weight = win_price_eur on wins. Higher = tighter implied ROAS floor sustained across the supply we bought; the headline budget-planning quality metric for A/B comparisons that hold spend equal. NEVER arithmetic-average across cells client-side — it's a geometric mean and the bias would mislead.",
    sql: "selectivity",
    agg: "none",
    kind: "discrete",
    format: "0.000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "campaign_type", "primary_prediction_flight_id"],
  },
  // --- Set 1: Within-minute stability ---
  // Measures how much spend_adjustment varies between individual bids inside each 1-minute bucket.
  // Per (budget, flight): compute variance per minute → take daily P50/P99 → weighted avg by supply cost.
  spend_adjustment_variance_p50: {
    id: "spend_adjustment_variance_p50",
    label: "Pacing Stability: Variance Within Minute (P50)",
    description:
      "Within-minute variance of spend adjustment. Per (budget, primary prediction flight): variance is computed inside each 1-minute bucket across individual bids, the P50 (median) of those per-minute variances is taken for the day, then averaged across (budget, primary prediction flight) pairs weighted by supply cost.",
    sql: "sa_var_within_minute_p50",
    agg: "none",
    kind: "discrete",
    format: "0.000000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  spend_adjustment_variance_p99: {
    id: "spend_adjustment_variance_p99",
    label: "Pacing Stability: Variance Within Minute (P99)",
    description:
      "Within-minute variance of spend adjustment. Per (budget, primary prediction flight): variance is computed inside each 1-minute bucket across individual bids, the P99 of those per-minute variances is taken for the day, then averaged across (budget, primary prediction flight) pairs weighted by supply cost.",
    sql: "sa_var_within_minute_p99",
    agg: "none",
    kind: "discrete",
    format: "0.000000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  // --- Set 2: Across-minute stability ---
  // Measures how much the minute-averaged spend_adjustment drifts across the day.
  // Per (budget, flight): average spend_adjustment per minute → compute supply-cost-weighted metric across minutes → simple avg across pacing units.
  spend_adjustment_variance_minute_avg: {
    id: "spend_adjustment_variance_minute_avg",
    label: "Pacing Stability: Variance Across Minutes",
    description:
      "Across-minute variance of spend adjustment. Per (budget, primary prediction flight): spend adjustment is averaged per 1-minute bucket, then the supply-cost-weighted variance of those minute-averages is computed for the day (minutes with more spend count more), then simple-averaged across (budget, primary prediction flight) pairs.",
    sql: "sa_var_across_minutes",
    agg: "none",
    kind: "discrete",
    format: "0.000000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  spend_adjustment_churn_minute_avg: {
    id: "spend_adjustment_churn_minute_avg",
    label: "Pacing Stability: Churn Across Minutes",
    description:
      "Across-minute churn of spend adjustment. Churn = supply-cost-weighted average of absolute steps between consecutive minute-averages (minutes with more spend count more). Per (budget, primary prediction flight): computed for the day, then simple-averaged across (budget, primary prediction flight) pairs.",
    sql: "sa_churn_across_minutes",
    agg: "none",
    kind: "discrete",
    format: "0.0000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  spend_adjustment_flip_rate_minute_avg: {
    id: "spend_adjustment_flip_rate_minute_avg",
    label: "Pacing Stability: Flip Rate Across Minutes",
    description:
      "Across-minute flip rate of spend adjustment. Flip rate = share of consecutive minute-average deltas that reverse sign. Per (budget, primary prediction flight): computed for the day, then simple-averaged across (budget, primary prediction flight) pairs.",
    sql: "sa_flip_rate_across_minutes",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  // --- Pacing error: Across minutes (day-level) ---
  // Aggregates actual/target spend to the day per pacing unit = (budget, flight),
  // then computes deviation, then supply-cost-weighted avg across pacing units.
  pacing_error_deviation_across_minutes: {
    id: "pacing_error_deviation_across_minutes",
    label: "Pacing Error: Deviation Across Minutes",
    description:
      "Daily pacing error. Per pacing unit (budget, primary prediction flight): aggregate actual and target spend for the day, compute |actual − target| / target, then supply-cost-weighted average across pacing units. Captures whether the day-level budget target was met.",
    sql: "pe_deviation_across_minutes",
    agg: "none",
    kind: "discrete",
    format: "0.0000",
    owner: "Freddie",
    tags: ["Bidding Logic"],
    filterDimensions: ["all", "organization", "budget_id", "primary_prediction_flight_id"],
  },
  repeated_win_loss_correction_coefficient: {
    id: "repeated_win_loss_correction_coefficient",
    label: "Repeated Win/Loss Correction Multiplier",
    description:
      "Multiplier that reduces (0-1) our impression value the more impressions a user+campaign has had recently, or increases it (1+) if we haven't won a user+audience multiple times in a row.",
    sql: "correction_coefficient",
    agg: "avg",
    kind: "continuous",
    format: "0.00",
    owner: "Tom",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    meanExpr: "correction_coefficient_mean",
    stdExpr: "correction_coefficient_sd",
  },
  max_bid: {
    id: "max_bid",
    label: "Max Bid (CPM)",
    description:
      "Max Bid (Adjusted Bid) is the result produced by the MaxBidPredictor. The value is the maximum price we are willing to pay for 1000 impressions (CPM), taking all relevant budget- related control into account. It represents the outcome of the formula impressionValue * (1- targetMargin)* spendAdjustment",
    sql: "adjusted_bid",
    agg: "avg",
    kind: "continuous",
    unit: "EUR",
    format: "€0,0.00",
    owner: "Johannes",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    meanExpr: "adjusted_bid_mean / 1000000",
    stdExpr: "adjusted_bid_sd / 1000000",
  },
  impression_value_shading_factor: {
    id: "impression_value_shading_factor",
    label: "Bid shading: bid price / impression value",
    description: "How high is the bid price compared to our estimated worth of the impression.",
    agg: "avg",
    kind: "continuous",
    unit: "ratio",
    format: "0.00%",
    owner: "Alfred",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    sql: "impression_value_shading_factor",
  },
  max_bid_shading_factor: {
    id: "max_bid_shading_factor",
    label: "Bid shading: bid price / max bid",
    description: "How high is the bid price compared to the Max Bid",
    agg: "avg",
    kind: "continuous",
    unit: "ratio",
    format: "0.00%",
    owner: "Alfred",
    tags: ["Bidding Logic"],
    normalizeBy: "bids_sum",
    sql: "max_bid_shading_factor",
  },
  total_bid_multiplier: {
    id: "total_bid_multiplier",
    label: "Total Bid Multipliers",
    description:
      "Product of all bid multipliers (auto, manual, publisher, spend, conversion estimator) relative to CPC.",
    sql: "total_bid_multiplier",
    agg: "avg",
    kind: "continuous",
    format: "0.00",
    normalizeBy: "bids_sum",
    owner: "Pablo",
    tags: ["Bidding Logic"],
    meanExpr: "total_bid_multiplier_mean / 100", // stored in percentage points: 100 means CPC * 1.0
    stdExpr: "total_bid_multiplier_sd / 100",
  },
  // Unique counts metrics (only available for: ad_type, placement_type, country, audience)
  unique_users_bid: {
    id: "unique_users_bid",
    label: "Unique Users (Bid)",
    description: "Number of unique users who received a bid.",
    sql: "unique_users_bid",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_impression: {
    id: "unique_users_impression",
    label: "Unique Users (Impression)",
    description: "Number of unique users who saw an impression.",
    sql: "unique_users_impression",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_click: {
    id: "unique_users_click",
    label: "Unique Users (Click)",
    description: "Number of unique users who clicked.",
    sql: "unique_users_click",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_reengagement: {
    id: "unique_users_reengagement",
    label: "Unique Users (Reengagement)",
    description: "Number of unique users who reengaged.",
    sql: "unique_users_reengagement",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_partner_reengagement: {
    id: "unique_users_partner_reengagement",
    label: "Unique Users (Partner Reengagement)",
    description: "Number of unique users with partner reengagement.",
    sql: "unique_users_partner_reengagement",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_conversions: {
    id: "unique_users_conversions",
    label: "Unique Users (Conversions)",
    description: "Number of unique users who converted.",
    sql: "unique_users_conversions",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_ap_conversions: {
    id: "unique_users_ap_conversions",
    label: "Unique Users (AP Conversions)",
    description: "Number of unique users with MMP-attributed conversions.",
    sql: "unique_users_ap_conversions",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_revenue: {
    id: "unique_users_revenue",
    label: "Unique Users (Revenue)",
    description: "Number of unique users who generated revenue.",
    sql: "unique_users_revenue",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_ap_revenue: {
    id: "unique_users_ap_revenue",
    label: "Unique Users (AP Revenue)",
    description: "Number of unique users with MMP-attributed revenue.",
    sql: "unique_users_ap_revenue",
    agg: "sum",
    kind: "discrete",
    unit: "count",
    isCumulative: true,
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_impression_rate: {
    id: "unique_users_impression_rate",
    label: "Unique Users Impression Rate",
    description: "Percentage of unique users who received a bid that saw an impression.",
    sql: "unique_users_impression / nullif(unique_users_bid, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
  unique_users_click_rate: {
    id: "unique_users_click_rate",
    label: "Unique Users Click Rate",
    description: "Percentage of unique users who received a bid that clicked.",
    sql: "unique_users_click / nullif(unique_users_bid, 0)",
    agg: "none",
    kind: "discrete",
    unit: "ratio",
    format: "0.00%",
    owner: "David",
    tags: ["Advertising Performance"],
    filterDimensions: ["ad_type", "placement_type", "country", "audience"],
  },
};

export const metricModel: MetricModel = {
  namespace: "wpr",
  dimensions,
  measures,
};

export type MeasureId = keyof typeof measures;
export type DimensionId = keyof typeof dimensions;

export const MEASURES: MeasureDef[] = Object.values(measures);
export const DISCRETE_MEASURES: MeasureDef[] = MEASURES.filter(measure => measure.kind === "discrete");
export const CONTINUOUS_MEASURES: MeasureDef[] = MEASURES.filter(measure => measure.kind === "continuous");

export const DIMENSIONS = Object.values(dimensions);

export type MetricConfig = MeasureDef;

export const isDiscreteMeasure = (measure: MeasureDef): measure is MeasureDef & { kind: "discrete" } =>
  measure.kind === "discrete";

export const isContinuousMeasure = (measure: MeasureDef): measure is MeasureDef & { kind: "continuous" } =>
  measure.kind === "continuous";
